import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import gsap from 'gsap'

/** Gap kept from the viewport edge when clamping a dropped panel back into view. */
const VIEWPORT_MARGIN_PX = 12
const BOUNCE_DURATION_S = 0.6
const BOUNCE_EASE = 'elastic.out(1, 0.5)'

export type UseDraggableFloatingPanelReturn<PanelEl extends HTMLElement, HandleEl extends HTMLElement> = {
    /** Attach to the panel's root element — dragging moves this via a translate. */
    panelRef: RefObject<PanelEl>
    /** Attach to the draggable header/handle inside the panel. */
    handleRef: RefObject<HandleEl>
    isDragging: boolean
}

const getAxisProperty = (el: HTMLElement, prop: 'x' | 'y') => (gsap.getProperty(el, prop) as number) ?? 0

/**
 * Makes a floating panel draggable by its header, OS-window style. Drag is a
 * gsap `x`/`y` transform on top of the panel's own `position: fixed` layout,
 * so it composes with any base placement. Releasing the panel with any edge
 * past the viewport bounds snaps it fully back into view with a gsap
 * elastic tween.
 */
export const useDraggableFloatingPanel = <
    PanelEl extends HTMLElement = HTMLDivElement,
    HandleEl extends HTMLElement = HTMLDivElement,
>(
    active = true,
): UseDraggableFloatingPanelReturn<PanelEl, HandleEl> => {
    const panelRef = useRef<PanelEl>(null)
    const handleRef = useRef<HandleEl>(null)
    const dragStartRef = useRef({ pointerX: 0, pointerY: 0, x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)

    const bounceBackIfOffscreen = useCallback(() => {
        const panel = panelRef.current
        if (!panel) return

        const rect = panel.getBoundingClientRect()
        const currentX = getAxisProperty(panel, 'x')
        const currentY = getAxisProperty(panel, 'y')

        let deltaX = 0
        let deltaY = 0

        if (rect.left < VIEWPORT_MARGIN_PX) {
            deltaX = VIEWPORT_MARGIN_PX - rect.left
        } else if (rect.right > window.innerWidth - VIEWPORT_MARGIN_PX) {
            deltaX = window.innerWidth - VIEWPORT_MARGIN_PX - rect.right
        }

        if (rect.top < VIEWPORT_MARGIN_PX) {
            deltaY = VIEWPORT_MARGIN_PX - rect.top
        } else if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN_PX) {
            deltaY = window.innerHeight - VIEWPORT_MARGIN_PX - rect.bottom
        }

        if (deltaX === 0 && deltaY === 0) return

        gsap.to(panel, {
            x: currentX + deltaX,
            y: currentY + deltaY,
            duration: BOUNCE_DURATION_S,
            ease: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'power2.out' : BOUNCE_EASE,
        })
    }, [])

    useEffect(() => {
        if (!active) return
        const handle = handleRef.current
        const panel = panelRef.current
        if (!handle || !panel) return

        const onPointerMove = (event: PointerEvent) => {
            const { pointerX, pointerY, x, y } = dragStartRef.current
            gsap.set(panel, {
                x: x + (event.clientX - pointerX),
                y: y + (event.clientY - pointerY),
            })
        }

        const endDrag = () => {
            document.removeEventListener('pointermove', onPointerMove)
            document.removeEventListener('pointerup', endDrag)
            document.body.style.userSelect = ''
            setIsDragging(false)
            bounceBackIfOffscreen()
        }

        const onPointerDown = (event: PointerEvent) => {
            // Let interactive controls in the header (e.g. the close button) work.
            if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return
            if (event.button !== 0) return

            dragStartRef.current = {
                pointerX: event.clientX,
                pointerY: event.clientY,
                x: getAxisProperty(panel, 'x'),
                y: getAxisProperty(panel, 'y'),
            }
            document.body.style.userSelect = 'none'
            setIsDragging(true)
            document.addEventListener('pointermove', onPointerMove)
            document.addEventListener('pointerup', endDrag)
        }

        handle.addEventListener('pointerdown', onPointerDown)
        return () => {
            handle.removeEventListener('pointerdown', onPointerDown)
            document.removeEventListener('pointermove', onPointerMove)
            document.removeEventListener('pointerup', endDrag)
        }
    }, [active, bounceBackIfOffscreen])

    useEffect(() => {
        if (!active) return
        window.addEventListener('resize', bounceBackIfOffscreen)
        return () => window.removeEventListener('resize', bounceBackIfOffscreen)
    }, [active, bounceBackIfOffscreen])

    return { panelRef, handleRef, isDragging }
}
